import { useQuery } from '@tanstack/react-query';
import { getTokenFromStorage } from '@/utils/auth';
import { getCampaignBeat } from '@/api/beatlyApi';
import type { ClientCampaignBeat } from '@/api/beatlyApi';

export interface UseCampaignBeatParams {
  beatId: string | undefined;
  enabled?: boolean;
}

function isValidBeatId(id: string | undefined): boolean {
  return Boolean(id && id.length >= 8 && id.includes('-'));
}

/**
 * Fetches a single beat by id (includes beat brief and campaign brief).
 * GET /app/v2/client/campaign/beat/:beatId
 */
export function useCampaignBeat({ beatId, enabled = true }: UseCampaignBeatParams) {
  const token = getTokenFromStorage();
  const shouldFetch = enabled && Boolean(token && beatId) && isValidBeatId(beatId);

  const { data, isLoading, error, isError } = useQuery({
    queryKey: ['client', 'campaign', 'beat', beatId],
    queryFn: async () => {
      if (!beatId) throw new Error('Beat ID is required');
      return getCampaignBeat(beatId, token ?? '');
    },
    enabled: shouldFetch,
  });

  return {
    data: data as ClientCampaignBeat | undefined,
    isLoading,
    error,
    isError,
  };
}
